import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  CssBaseline,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { isDepreciatedVideo, readDepreciatedProducts, stillDependsOn } from '../ps/format';
import psTheme, { surfaces } from '../ps/psTheme';
import '../ps/ps-tokens.css';
import '../ps/learn.css';
import VPlayerOverlay from './VPlayerOverlay';
import VideoCard from './VideoCard';
import pslogo from './pslogo.png';
import LoadingAnimation from './LoadingAnimation';
import Footer from './Footer';

// The config API sends the hero crop offsets as bare numbers, sometimes as
// strings that already carry a unit.
const toLength = (value) =>
  value === undefined || value === null || value === '' ? '0px'
    : typeof value === 'number' ? `${value}px`
      : String(value);

// A section is a caption, a hairline and a count. No 28px headline over a row
// of things that already wear their names.
const Rail = ({ title, videos, progressData, onOpen }) => {
  if (!videos.length) return null;
  return (
    <section className="learn-section">
      <div className="learn-section__head">
        <span className="ps-label">{title}</span>
        <span className="learn-section__rule" />
        <span className="ps-value" style={{ fontSize: 12.5 }}>{videos.length}</span>
      </div>
      <div className="learn-rail ps-scroll">
        {videos.map((video) => (
          <VideoCard
            key={video.identifier}
            video={video}
            progress={progressData[video.identifier] || 0}
            onOpen={onOpen}
          />
        ))}
      </div>
    </section>
  );
};

const Home = () => {
  const [userData, setUserData] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userOwned, setUserOwned] = useState({});
  const [videoData, setVideoData] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productInfo, setProductInfo] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showDepreciated, setShowDepreciated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [selectedVideoData, setSelectedVideoData] = useState(null);
  const [progressData, setProgressData] = useState({});
  const [FeaturedVideo, setFeaturedVideo] = useState('');
  const [FeaturedVideoOffset, setFeaturedVideoOffset] = useState(0);
  const [FeaturedVideoOffsetxs, setFeaturedVideoOffsetxs] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [videoOrder, setVideoOrder] = useState([]);
  const [secret] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Track when loading started
  const loadingStartTime = useRef(null);

  const startLoading = () => {
    if (!loadingStartTime.current) {
      loadingStartTime.current = Date.now();
    }
    setLoading(true);
  };

  const stopLoading = () => {
    // LoadingAnimation will handle timing and call handleAnimationComplete when done
    // Just mark that we're ready to stop
    loadingStartTime.current = null;
  };

  const handleAnimationComplete = () => {
    // Called by LoadingAnimation after 2500ms (2s animation + 500ms fade)
    setLoading(false);
  };

  const handlePasswordVisibilityToggle = () => {
    setShowPassword((prev) => !prev);
  };

  const fetchFeaturedVideo = async () => {
    try {
      const response = await axios.get(`https://serve.philspeiser.com/api/getconfig`);
      setFeaturedVideo(response.data.featuredvideo);
      setFeaturedVideoOffset(response.data.featuredvideooffset);
      setFeaturedVideoOffsetxs(response.data.featuredvideooffsetxs);
      setVideoOrder(response.data.videoOrder);
      // Awaited, so the catalogue and the product records land in the same
      // paint. Arriving late, the depreciated set would briefly be empty and the
      // retired shelf would flash into the rails before folding itself away.
      await fetchProductInfo();
    } catch (err) {
      setNotification({
        open: true,
        message: 'Failed to fetch featured video.',
        severity: 'error',
      });
    }
  };

  const fetchProductInfo = async () => {
    try {
      const response = await axios.get('https://psproducts.vercel.app/api/getproducts');
      localStorage.setItem('productInfo', JSON.stringify(response.data.products));
      setProductInfo(response.data.products || {});
    } catch (err) {
      setNotification({
        open: true,
        message: 'Failed to fetch product info.',
        severity: 'error',
      });
    }
  }

  const fetchProgressData = async () => {
    try {
      const response = await axios.get(`https://serve.philspeiser.com/api/getvideoprogress?email=${userData?.email}&name=${userData?.displayName}`);
      setProgressData(response.data);
    } catch (err) {
      setNotification({
        open: true,
        message: 'Failed to fetch user progress data.',
        severity: 'error',
      });
    }
  };

  const fetchVideoData = async () => {
    const url = new URL(window.location);
    url.searchParams.delete('email');
    url.searchParams.delete('token');
    window.history.replaceState({}, document.title, url.pathname);

    try {
      const response = await axios.get('https://learn.philspeiser.com/api/getall');
      const data = response.data || {};

      // Replace Vercel Blob Storage URLs with BunnyCDN for all videos
      Object.keys(data).forEach(key => {
        const video = data[key];

        // Replace thumbnailUrl
        if (video.thumbnailUrl && video.thumbnailUrl.includes('i6bcizwg1mpghn9b.public.blob.vercel-storage.com')) {
          video.thumbnailUrl = video.thumbnailUrl.replace(
            'i6bcizwg1mpghn9b.public.blob.vercel-storage.com',
            'psblob.b-cdn.net'
          );
        }

        // Replace minithumbnailUrl
        if (video.minithumbnailUrl && video.minithumbnailUrl.includes('i6bcizwg1mpghn9b.public.blob.vercel-storage.com')) {
          video.minithumbnailUrl = video.minithumbnailUrl.replace(
            'i6bcizwg1mpghn9b.public.blob.vercel-storage.com',
            'psblob.b-cdn.net'
          );
        }
      });

      return data;
    } catch (err) {
      setNotification({
        open: true,
        message: 'Failed to fetch video data.',
        severity: 'error',
      });
      return {};
    }
  };

  const processVideoData = (fetchedData, userOwnedData) => {
    const updatedData = Object.keys(fetchedData).reduce((acc, key) => {
      const video = fetchedData[key];
      if (video.products) {
        video.products = video.products.split(/\s*,\s*/).map((product) => product.trim());
      } else {
        video.products = [];
      }

      let owned = false;
      if (video.public === 'true') {
        owned = true;
      } else if (userOwnedData.subscription === 'active') {
        owned = true;
      } else if (userOwnedData.products) {
        owned = video.products.some((product) => userOwnedData.products.includes(product));
      }

      acc[key] = { ...video, owned };
      return acc;
    }, {});

    setVideoData(updatedData);

    // Extract and sort product tags alphabetically
    const allProducts = [
      ...new Set(Object.values(updatedData).flatMap((video) => video.products)),
    ].sort((a, b) => a.localeCompare(b));
    setProducts(allProducts);

    const categoryList = [...new Set(Object.values(updatedData).map((video) => video.category))];
    categoryList.sort((a, b) => {
      if (a === "MASTERCLASS") return -1;
      if (b === "MASTERCLASS") return 1;

      if (a === "WALKTHROUGH") return -1;
      if (b === "WALKTHROUGH") return 1;

      if (a === "TUTORIAL") return -1;
      if (b === "TUTORIAL") return 1;

      if (a === "QUICK TIP") return -1;
      if (b === "QUICK TIP") return 1;

      return 0;
    });

    setCategories(categoryList);
  };


  const fetchUserOwned = async () => {
    try {
      const response = await axios.post('https://psproducts.vercel.app/api/ownedfortoken', {
        token: userData.token,
      });
      localStorage.setItem('userOwnedProducts', JSON.stringify(response.data));
      return response.data;
    } catch (err) {
      setNotification({
        open: true,
        message: 'Failed to fetch user owned data.',
        severity: 'error',
      });
      return { products: [], subscription: null };
    }
  };

  const attemptTokenLogin = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlEmail = urlParams.get('email');
    const urlToken = urlParams.get('token');

    // If user is not logged in and URL params are present, try token login
    if (!userData && urlEmail && urlToken) {
      startLoading();
      try {
        const response = await axios.post('https://psproducts.vercel.app/api/tokenauth', {
          email: urlEmail,
          token: urlToken,
        });

        const newUserData = {
          email: response.data.user.username,
          displayName: response.data.user.first_name,
          token: response.data.user.token,
        };
        localStorage.setItem('userData', JSON.stringify(newUserData));
        setUserData(newUserData);

        // Remove the parameters after successful login
        const url = new URL(window.location);
        url.searchParams.delete('email');
        url.searchParams.delete('token');
        window.history.replaceState({}, document.title, url.pathname);

        setNotification({
          open: true,
          message: 'Login successful.',
          severity: 'success',
        });
      } catch (err) {
        // token login failed => show login
        stopLoading();
      }
    } else {
      // If already logged in but URL still has params, remove them
      if (userData && (urlParams.has('email') || urlParams.has('token'))) {
        const url = new URL(window.location);
        url.searchParams.delete('email');
        url.searchParams.delete('token');
        window.history.replaceState({}, document.title, url.pathname);
      }

      // If no userData, just show login form
      if (!userData) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      // We have userData => start loading and fetch data
      startLoading();
      setUserData(JSON.parse(storedUserData));
      setIsInitializing(false);
    } else {
      // No userData, try token login
      attemptTokenLogin();
      setIsInitializing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userData) {
      startLoading();

      // Load ALL data in parallel - maximum performance!
      // This reduces total load time from ~1,800ms to just ~800ms (the slowest API call)
      Promise.all([
        fetchUserOwned(),
        fetchVideoData(),
        fetchFeaturedVideo(),
        fetchProgressData()
      ])
        .then(([userOwnedData, videoDataRaw]) => {
          // Once all data is loaded, process it together
          setUserOwned(userOwnedData);
          processVideoData(videoDataRaw, userOwnedData);
          stopLoading();
        })
        .catch((error) => {
          stopLoading();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  useEffect(() => {
    if (isOverlayOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOverlayOpen]);

  const handleLogin = async (e) => {
    e.preventDefault();
    startLoading();
    try {
      const response = await axios.post('https://psproducts.vercel.app/api/auth', {
        email: email,
        password: password,
      });

      const userDataResponse = {
        email: response.data.user.username,
        displayName: response.data.user.first_name,
        token: response.data.user.token,
      };

      localStorage.setItem('userData', JSON.stringify(userDataResponse));
      setUserData(userDataResponse);
      setNotification({
        open: true,
        message: 'Login successful.',
        severity: 'success',
      });
      // After login owned and videos will be fetched, stopLoading will be called then
    } catch (err) {
      setNotification({
        open: true,
        message: 'Login failed. Please check your credentials.',
        severity: 'error',
      });
      // No data to fetch => stop loading
      stopLoading();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userData');
    setUserData(null);
    window.location.reload();
  };

  const handleOpenOverlay = (video) => {
    if (video.owned) {
      const progress = progressData[video.identifier] || 0;
      setSelectedVideoData({ ...video, progress });
      setIsOverlayOpen(true);
    }
  };

  const handleCloseOverlay = () => {
    setIsOverlayOpen(false);
    setSelectedVideoData(null);
    fetchProgressData();
  };

  const handleProductTagClick = (product) => {
    setSelectedProducts((prevSelected) =>
      prevSelected.includes(product)
        ? prevSelected.filter((item) => item !== product)
        : [...prevSelected, product]
    );
  };

  const sortVideos = (videos, order) => {
    const orderMap = new Map();
    order.forEach((id, index) => {
      orderMap.set(id, index);
    });

    return [...videos].sort((a, b) => {
      const aIndex = orderMap.has(a.identifier) ? orderMap.get(a.identifier) : Infinity;
      const bIndex = orderMap.has(b.identifier) ? orderMap.get(b.identifier) : Infinity;
      return aIndex - bIndex;
    });
  };

  const moveOwnedToTop = (videos) => {
    const owned = videos.filter(video => video.owned);
    const notOwned = videos.filter(video => !video.owned);
    return [...owned, ...notOwned];
  };

  // Retiring a plugin does not retire its videos for the people still running
  // it. The upgrade does: own THE_SHAPER and THE_SHAPER 2 and the v1
  // walkthroughs move to the legacy rail; own only THE_SHAPER and they stay
  // where they are, because they are still your manual. This reads
  // entitlements rather than `owned` -- `owned` is true for every subscriber,
  // and a subscription is not a reason to be handed the back catalogue.
  const depreciatedProducts = readDepreciatedProducts(productInfo);
  const survivesDeprecation = (video) =>
    !isDepreciatedVideo(video, depreciatedProducts) ||
    stillDependsOn(video.products, userOwned.products);

  const matchesFilter = (video) =>
    selectedProducts.length === 0 ||
    video.products.some((product) => selectedProducts.includes(product));

  // A locked card may not advertise a plugin nobody can buy: that call to
  // action leads to a dead store page, which is a support email.
  const prepare = (list) =>
    moveOwnedToTop(sortVideos(list, videoOrder)).map((video) => ({
      ...video,
      unlockWith: video.products.filter((product) => !depreciatedProducts.has(product)),
    }));

  const live = Object.values(videoData).filter((video) => video.frontend === 'true');
  const sortedVideos = prepare(
    live.filter((video) => survivesDeprecation(video) && matchesFilter(video))
  );

  // One shelf at the foot of the page, rather than retired videos sprinkled
  // back through the rails: put back in place they are indistinguishable from
  // current material, which is the thing being fixed.
  const legacyVideos = prepare(
    live.filter((video) => !survivesDeprecation(video) && matchesFilter(video))
  );

  // A retired plugin keeps its filter pill only while something it names is
  // still on screen -- a pill that filters to nothing is a dead end. It can
  // survive its own retirement by sitting on a video that also names a live
  // plugin, which is why this asks the videos rather than the product. Once
  // the shelf is open, its pills come back with it.
  const pillSurvives = (product) =>
    live.some((video) => video.products.includes(product) && survivesDeprecation(video));

  const visibleProducts = products.filter(
    (product) => !depreciatedProducts.has(product) || showDepreciated || pillSurvives(product)
  );

  const handleDepreciatedToggle = () => {
    const next = !showDepreciated;
    setShowDepreciated(next);
    // Closing the shelf must not leave a filter selected whose pill just went
    // away: that reads as an empty page with no visible way back.
    if (!next) {
      setSelectedProducts((prev) => prev.filter(pillSurvives));
    }
  };

  const featured = videoData[FeaturedVideo];
  const featuredProgress = featured ? progressData[featured.identifier] || 0 : 0;

  const renderHero = () => {
    if (!featured) return null;
    // The hero is looked up by id, not taken from the filtered catalogue, so
    // retirement has to be checked again here -- otherwise a superseded video
    // gets pitched, full width, at people who have already moved on from it.
    if (!survivesDeprecation(featured)) return null;
    const locked = !featured.owned;
    const unlockWith = featured.products.filter((product) => !depreciatedProducts.has(product));

    return (
      <Box
        component={locked ? 'div' : 'button'}
        type={locked ? undefined : 'button'}
        className="learn-hero"
        onClick={locked ? undefined : () => handleOpenOverlay(featured)}
        sx={locked ? { cursor: 'default' } : undefined}
      >
        {/* The two crop offsets are editorial, set per featured video in the
            admin. They ride in as custom properties so the breakpoint stays in
            CSS -- read from `window.innerWidth` at render time, the wide crop
            stuck until the next re-render after a rotate. */}
        <img
          className="learn-hero__img"
          src={featured.thumbnailUrl}
          alt=""
          loading="eager"
          style={{
            '--hero-offset': toLength(FeaturedVideoOffset),
            '--hero-offset-xs': toLength(FeaturedVideoOffsetxs),
          }}
        />
        <span className="learn-hero__veil" />
        <span className="learn-hero__tag">Featured</span>

        <div className="learn-hero__body">
          <span className="learn-hero__title">{featured.title}</span>
          {featured.description && (
            <span style={{ fontSize: 13, color: 'var(--ps-text-2)', maxWidth: '64ch' }}>
              {featured.description}
            </span>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '4px' }}>
            {locked ? (
              <>
                <LockOutlinedIcon sx={{ fontSize: 16, color: 'var(--ps-text-dim)' }} />
                <span className="ps-label ps-label--dim">
                  {unlockWith.length
                    ? `Unlock with ${unlockWith.join(' · ')}`
                    : 'No longer for sale'}
                </span>
              </>
            ) : (
              <>
                <PlayArrowIcon sx={{ fontSize: 18, color: 'var(--ps-gold)' }} />
                <span className="ps-label" style={{ color: 'var(--ps-gold)' }}>
                  {featuredProgress > 0 && featuredProgress < 95 ? 'Resume' : 'Watch now'}
                </span>
              </>
            )}
          </Box>
        </div>

        {featuredProgress > 0 && !locked && (
          <span
            className="learn-card__elapsed"
            style={{ width: `${Math.min(100, featuredProgress)}%` }}
          />
        )}
      </Box>
    );
  };

  const renderMainContent = () => (
    <main className="learn-main">
      {userOwned.subscription !== 'active' && <Footer />}

      {renderHero()}

      {/* Filters are pills: lit gold when on, a hairline chip when not. */}
      {visibleProducts.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: '4px',
            mb: '22px',
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            overflowX: { xs: 'auto', sm: 'visible' },
            pb: { xs: '4px', sm: 0 },
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {visibleProducts.map((product) => {
            const on = selectedProducts.includes(product);
            return (
              <Button
                key={product}
                size="small"
                variant={on ? 'contained' : 'outlined'}
                onClick={() => handleProductTagClick(product)}
                aria-pressed={on}
                sx={{ borderRadius: 999, flexShrink: 0 }}
              >
                {product}
              </Button>
            );
          })}
        </Box>
      )}

      {categories.map((category) => (
        <Rail
          key={category}
          title={category}
          videos={sortedVideos.filter((video) => video.category === category)}
          progressData={progressData}
          onOpen={handleOpenOverlay}
        />
      ))}

      {/* A drawer at the foot of the page, not a filter in the row above: no
          face until hover, dim text, a chevron that turns. It reads as "there
          is more below", which is what it is -- and the count in the closed
          label is the whole reason to open it. */}
      {legacyVideos.length > 0 && (
        <Box sx={{ mt: '22px' }}>
          <button
            type="button"
            className="ps-chip ps-chip--quiet"
            onClick={handleDepreciatedToggle}
            aria-expanded={showDepreciated}
            style={{ gap: '6px', paddingLeft: 8 }}
          >
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 16,
                transition: 'transform 160ms cubic-bezier(0.22, 1, 0.36, 1)',
                transform: showDepreciated ? 'rotate(180deg)' : 'none',
              }}
            />
            {showDepreciated
              ? 'Hide older versions'
              : `${legacyVideos.length} video${legacyVideos.length > 1 ? 's' : ''} for older versions`}
          </button>
        </Box>
      )}

      {showDepreciated && (
        <Rail
          title="Older versions"
          videos={legacyVideos}
          progressData={progressData}
          onOpen={handleOpenOverlay}
        />
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ minWidth: 280, boxShadow: '0 8px 28px rgba(0,0,0,0.55)' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {isOverlayOpen && selectedVideoData && (
        <VPlayerOverlay
          identifier={selectedVideoData.identifier}
          progress={selectedVideoData.progress}
          onClose={handleCloseOverlay}
          isPromo={false}
        />
      )}
    </main>
  );

  const renderLoginForm = () => (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: '10px',
      }}
    >
      <Box
        component="form"
        onSubmit={handleLogin}
        sx={{ ...surfaces.panel, p: '22px', width: 360, maxWidth: '100%' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', mb: '22px' }}>
          <img src={pslogo} alt="Phil Speiser" style={{ height: 20 }} />
          <span className="ps-label">Sign in to watch</span>
        </Box>

        <TextField
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          size="small"
          name="email"
          autoComplete="email"
          InputLabelProps={{ shrink: true }}
          sx={{ mb: '10px' }}
        />
        <TextField
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          size="small"
          name="password"
          autoComplete="current-password"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handlePasswordVisibilityToggle}
                  edge="end"
                  size="small"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <VisibilityOff sx={{ fontSize: 17 }} /> : <Visibility sx={{ fontSize: 17 }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: '22px' }}
        />

        <Button type="submit" variant="contained" fullWidth>
          Log in
        </Button>
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ minWidth: 280 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );

  // Don't render anything while initializing to prevent flash
  if (isInitializing) {
    return (
      <ThemeProvider theme={psTheme}>
        <CssBaseline />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={psTheme}>
      <CssBaseline />
      <div className="learn-root">
        {userData && (
          <header className="learn-header">
            <img className="learn-header__logo" src={pslogo} alt="Phil Speiser" />
            <Box sx={{ flex: 1 }} />
            {userData.displayName && (
              <span className="ps-label ps-label--dim" style={{ marginRight: 4 }}>
                {userData.displayName}
              </span>
            )}
            <Button variant="text" onClick={handleLogout}>
              Log out
            </Button>
          </header>
        )}

        {!secret && (userData ? renderMainContent() : renderLoginForm())}

        {(userData && loading) && (
          <LoadingAnimation onAnimationEnd={handleAnimationComplete} />
        )}
      </div>
    </ThemeProvider>
  );
};

export default Home;
